import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../AppContext';
import { Panels } from '../Consts';
import {
  Button, FormItem,
  FormLayoutGroup, Panel, PullToRefresh
} from '@vkontakte/vkui';
import { UserSelectComponent } from './components/UserSelectComponent';
import { AmountInputComponent } from './components/AmountInputComponent';
import { CommentInputComponent } from './components/CommentInputComponent';
import { DateInputComponent } from './components/DateInputComponent';
import { clearCache as eventInfoCacheClear } from '../Events/ShowEventPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { getResponseFromError } from '../../helpers/helpers';
import { MAX_AMOUNT, MIN_AMOUNT } from '../../Consts';
import { ImageUploadComponent } from './components/ImageUploadComponent';
import { SelectImagesDeleteComponent } from '../components/SelectImagesDeleteComponent';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';

const CURRENT_PANEL = Panels.EDIT_REPAYMENT_PANEL;

const EditRepaymentPanel = (props) => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [userFrom, setUserFrom] = useState(0);
  const [userFromError, setUserFromError] = useState('');
  const [userToError, setUserToError] = useState('');
  const [userTo, setUserTo] = useState(0);
  const [amount, setAmount] = useState(0);
  const [amountErrorText, setAmountErrorText] = useState('');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [dateError, setDateError] = useState('');
  const [addImages, setAddImages] = useState([]);
  const [images, setImages] = useState([]);
  const [imagesError, setImagesError] = useState('');
  const [imagesForDelete, setImagesForDelete] = useState([]);
  const [send, setSend] = useState(false);

  const [users, setUsers] = useState({});
  const [itemsForSelectMembers, setItemsForSelectMembers] = useState([]);
  const [updateToken, setUpdateToken] = useState(null);

  const isFormSavedRef = useRef(false);
  const [errorInit, setErrorInit] = useState(null);

  const fetch = (spinner = true) => {
    if (spinner) {
      context.spinner(true);
    }

    context.init().then(() => {
      Promise.all([
        context.httpClient().getEventV3(params.eventId),
        context.httpClient().getRepayment(params.eventId, params.id),
      ]).then((results) => {
        const [event, item, itemUsers] = [results[0].data, results[1].data.item, results[1].data.users];
        const allUsers = {};
        itemUsers.forEach((item) => {
          allUsers[item.id] = item;
        });
        (event.users || []).forEach((item) => {
          allUsers[item.id] = item;
        });


        setUsers(allUsers);
        setItemsForSelectMembers(Object.values(allUsers).map((user) => {
          return {
            value: user.id,
            label: user.name,
            img: user.img,
            type: user.type,
          };
        }));

        setAmount(item.amount);
        setComment(item.comment);
        setImages(item.images);
        setDateTime(context.lang().dateFormatToInput(new Date(item.date)));
        setUserFrom(item.from);
        setUserTo(item.to);
        setUpdateToken(item.update_token);

        context.spinner(false);
        setErrorInit(null);
      })
      .catch((error) => {
        setErrorInit(error);
      });
    })
    .catch((error) => {
      setErrorInit(error);
    });
  }

  useEffect(() => {
    fetch();
  }, []);

  const save = (eventId, id, from, to, amount, date, comment, images, updateToken) => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setSend(true);
    context.spinner(true);
    context.httpClient()
      .updateRepaymentV3(eventId, id, from, to, amount, date, comment, images, updateToken)
      .then((response) => {
        eventInfoCacheClear(eventId, context.cache);
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);
        context.back();
      })
      .catch((error) => {
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);

        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setUserToError(response.to || '');
        delete response['to'];

        setUserFromError(response.from || '');
        delete response['from'];

        setAmountErrorText(response.amount || '');
        delete response['amount'];

        setDateError(response.date || '');
        delete response['date'];

        setCommentError(response.comment || '');
        delete response['comment'];

        const imagesErrors = [];
        Object.keys(response).forEach((key) => {
          const [name, index] = key.split('.', 2);
          switch (name) {
            case 'images':
              imagesErrors.push(response[key] || '');
              delete response[key];
              break;
          }
        });

        setImagesError(imagesErrors.join(','));

        if (response.update_token || null) {
          context.showError(null, (response.update_token || []).join(', '));
        } else if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });
  }

  const onSaveHandler = () => {
    save(params.eventId, params.id, userFrom, userTo, amount, dateTime, comment, images.map((img) => img.id), updateToken);
  }

  if (errorInit) {
    return (<ErrorLoadComponent
      error={errorInit}
      onFetch={fetch}
    />)
  }

  return (<Panel id={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={context.lang().getKey(CURRENT_PANEL, 'header')}
    />
    {context.snackbar}
    <form>
      <FormLayoutGroup mode="vertical">
        <PullToRefresh onRefresh={() => fetch(false)}>
          <UserSelectComponent
            label={context.lang().getKey(CURRENT_PANEL, 'user_from')}
            placeholder={context.lang().getKey(CURRENT_PANEL, 'user_from_placeholder')}
            error={userFromError}
            setError={setUserFromError}
            members={itemsForSelectMembers.filter((item) => item.value !== userTo)}
            value={userFrom}
            setValue={setUserFrom}
          />
          <UserSelectComponent
            label={context.lang().getKey(CURRENT_PANEL, 'user_to')}
            placeholder={context.lang().getKey(CURRENT_PANEL, 'user_to_placeholder')}
            error={userToError}
            setError={setUserToError}
            members={itemsForSelectMembers.filter((item) => item.value !== userFrom)}
            value={userTo}
            setValue={setUserTo}
            description={(users[userTo] || {}).payment_info || ''}
          />
          <AmountInputComponent
            error={amountErrorText}
            setError={setAmountErrorText}
            amount={amount}
            setAmount={setAmount}
            min={MIN_AMOUNT}
            max={MAX_AMOUNT}
            label={context.lang().getKey('app', 'amount_input_label')}
            errorTextMin={context.lang().getKey('errors', 'amount_error_min')}
            errorTextMax={context.lang().getKey('errors', 'amount_error_max')}
          />
          <DateInputComponent
            defaultDate={context.lang().dateFormatToInput(new Date())}
            error={dateError}
            setError={setDateError}
            dateTime={dateTime}
            setDateTime={setDateTime}
            label={context.lang().getKey('app', 'date_input_label')}
            errorTextMinValue={context.lang().getKey('errors', 'date_error_min')}
            errorTextMaxValue={context.lang().getKey('errors', 'date_error_max')}
          />
          <CommentInputComponent
            error={commentError}
            setError={setCommentError}
            comment={comment}
            setComment={setComment}
            label={context.lang().getKey('app', 'comment_input_label')}
            errorTextMaxLength={context.lang().getKey('errors', 'comment_error_max_length_255_char')}
          />
          <SelectImagesDeleteComponent
            value={images}
            setValue={setImages}
          />
          <ImageUploadComponent
            eventId={params.eventId}
            value={images}
            setValue={setImages}
            error={imagesError}
            setError={setImagesError}
            label={context.lang().getKey('app', 'image_input_label')}
            errorTextMaxSize={context.lang().getKey('errors', 'image_error_max_size')}
            errorTextType={context.lang().getKey('errors', 'image_error_type')}
            errorMaxCount={context.lang().getKey('errors', 'image_error_max_count')}
            buttonLabel={context.lang().getKey('app', 'image_button_label__empty')}
            buttonLabelSelect={context.lang().getKey('app', 'image_button_label__selected')}
            maxSize={context.store.maxFileSize}
          />
        </PullToRefresh>
        <FormItem>
          <Button
            size="l"
            stretched={true}
            loading={send}
            disabled={send || !amount || amount < MIN_AMOUNT || amount > MAX_AMOUNT || userFrom === userTo}
            onClick={onSaveHandler}
          >
            {context.lang().getKey(CURRENT_PANEL, 'button_save_label')}
          </Button>
        </FormItem>
      </FormLayoutGroup>
    </form>
  </Panel>);
}
export default EditRepaymentPanel;
